import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const sevClass = (s) => `sev-${s}`;

export default function Threats() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/threats", { params: { limit: 200, severity: filter === "all" ? undefined : filter } });
      setItems(data);
    } catch (err) {
      console.error("threats load failed", err);
    }
  }, [filter]);

  useEffect(() => {
    load();
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
  }, [load]);

  const counts = useMemo(() => {
    const c = { critical: 0, high: 0, medium: 0, low: 0 };
    items.forEach((t) => { c[t.severity] = (c[t.severity] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <Layout title="Threat Detection" subtitle="DETECTION">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {["critical", "high", "medium", "low"].map((s) => (
          <div key={s} data-testid={`sev-card-${s}`} className={`border rounded-sm p-4 fade-up ${sevClass(s)}`}>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em]">{s}</div>
            <div className="font-display text-3xl font-bold mt-1 tabular-nums">{counts[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">{items.length} EVENTS · LIVE STREAM</div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="severity-filter" className="w-44 bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            {["all", "critical", "high", "medium", "low"].map((o) => <SelectItem key={o} value={o} className="font-mono uppercase text-xs">{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-8 border border-zinc-800 rounded-sm bg-zinc-900/40 overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-500 bg-zinc-900/70">
            <div className="col-span-2">TIME</div>
            <div className="col-span-1">SEV</div>
            <div className="col-span-3">CATEGORY</div>
            <div className="col-span-2">SOURCE</div>
            <div className="col-span-2">DEST</div>
            <div className="col-span-1">CLOUD</div>
            <div className="col-span-1 text-right">ACTION</div>
          </div>
          <div className="max-h-[520px] overflow-y-auto font-mono text-[11px]" data-testid="threats-stream">
            {items.map((t) => (
              <button
                key={t.threat_id}
                data-testid={`threat-${t.threat_id}`}
                onClick={() => setSelected(t)}
                className={"w-full text-left grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800/70 hover:bg-zinc-900 " + (selected?.threat_id === t.threat_id ? "bg-zinc-900" : "")}
              >
                <div className="col-span-2 text-zinc-500">{t.timestamp.slice(11, 19)}</div>
                <div className="col-span-1"><span className={`px-1.5 py-0.5 border rounded-sm uppercase tracking-widest text-[10px] ${sevClass(t.severity)}`}>{t.severity[0].toUpperCase()}</span></div>
                <div className="col-span-3 text-zinc-200 whitespace-normal break-words">{t.category}</div>
                <div className="col-span-2 text-cyan-400 whitespace-normal break-words">{t.source_country} {t.source_ip}</div>
                <div className="col-span-2 text-zinc-300 whitespace-normal break-words">:{t.destination_port}</div>
                <div className="col-span-1 text-zinc-400 uppercase">{t.cloud}</div>
                <div className="col-span-1 text-right text-amber-400 uppercase">{t.action_taken.slice(0, 7)}</div>
              </button>
            ))}
            {items.length === 0 && <div className="text-zinc-500 px-4 py-10 text-center">awaiting events...</div>}
          </div>
        </div>

        {/* Detail panel */}
        <div className="col-span-12 lg:col-span-4 border border-zinc-800 rounded-sm bg-zinc-900/60 p-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-3">PACKET INSPECTION</div>
          {selected ? (
            <div className="space-y-3 font-mono text-xs">
              <Row k="threat_id" v={selected.threat_id} />
              <Row k="timestamp" v={selected.timestamp} />
              <Row k="severity" v={selected.severity} />
              <Row k="category" v={selected.category} />
              <Row k="source" v={`${selected.source_ip} (${selected.source_country})`} />
              <Row k="destination" v={`${selected.destination_ip}:${selected.destination_port}`} />
              <Row k="protocol" v={selected.protocol} />
              <Row k="cloud" v={selected.cloud} />
              <Row k="rule" v={selected.rule_matched} />
              <Row k="confidence" v={selected.confidence} />
              <Row k="packet_size" v={`${selected.packet_size}b`} />
              <Row k="action" v={selected.action_taken} />
              <pre data-testid="threat-detail-raw" className="mt-4 p-3 border border-zinc-800 rounded-sm bg-zinc-950 text-[10px] text-emerald-300 overflow-x-auto">
{JSON.stringify(selected, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="text-zinc-500 text-sm font-mono">select an event to inspect packet metadata</div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border-b border-zinc-800/60 pb-1.5">
      <span className="text-zinc-500 uppercase tracking-widest text-[10px]">{k}</span>
      <span className="text-zinc-200 whitespace-normal break-words">{String(v)}</span>
    </div>
  );
}
